import { useAuthStore } from "@/features/auth/auth.store";
import { authClient } from "@/shared/lib/axios";
import {
	type ConfirmPaymentRequest,
	type ConfirmPaymentResponse,
	ConfirmPaymentResponseSchema,
	type GyulPaymentRequest,
	type PaymentHistoryResponse,
	PaymentHistoryResponseSchema,
	type PaymentOrderResponse,
	PaymentOrderResponseSchema,
	type PlanPaymentRequest,
	type PlanPaymentResponse,
	PlanPaymentResponseSchema,
} from "./payment.types";

const getApiBase = () =>
	import.meta.env.VITE_API_URL || "http://localhost:4000";

function getAuthHeaders(): HeadersInit {
	const token = useAuthStore.getState().accessToken;
	return {
		"Content-Type": "application/json",
		...(token ? { Authorization: `Bearer ${token}` } : {}),
	};
}

export const paymentApi = {
	requestPlanPayment: async (request: PlanPaymentRequest): Promise<PlanPaymentResponse> => {
		const { data } = await authClient.post("/payments/plan", request);
		return PlanPaymentResponseSchema.parse(data);
	},

	requestGyulPayment: async (request: GyulPaymentRequest): Promise<PaymentOrderResponse> => {
		const { data } = await authClient.post("/payments/gyul", request);
		return PaymentOrderResponseSchema.parse(data);
	},

	confirmPayment: async (request: ConfirmPaymentRequest): Promise<ConfirmPaymentResponse> => {
		const { data } = await authClient.post("/payments/confirm", request);
		return ConfirmPaymentResponseSchema.parse(data);
	},

	getHistory: async (params?: { page?: number; limit?: number }): Promise<PaymentHistoryResponse> => {
		const { data } = await authClient.get("/payments/history", { params });
		return PaymentHistoryResponseSchema.parse(data);
	},
};

export async function registerBillingKeyForPlan(
	authKey: string,
	customerKey: string,
): Promise<{ success?: true; error?: string }> {
	const res = await fetch(`${getApiBase()}/payments/billing-key`, {
		method: "POST",
		headers: getAuthHeaders(),
		body: JSON.stringify({ authKey, customerKey }),
	});
	const data = await res.json();
	if (!res.ok) return { error: data.error || "빌링키 등록에 실패했습니다." };
	return { success: true };
}

export async function registerBillingKeyWithAuth(
	authKey: string,
	customerKey: string,
): Promise<{ success?: true; error?: string }> {
	const res = await fetch(`${getApiBase()}/payments/billing/register-with-auth`, {
		method: "POST",
		headers: getAuthHeaders(),
		body: JSON.stringify({ authKey, customerKey }),
	});
	const data = await res.json();
	if (!res.ok) return { error: data.error || "빌링키 등록에 실패했습니다." };
	return { success: true };
}

export async function getBillingKey(): Promise<{
	cardCompany?: string | null;
	cardNumber?: string | null;
	authenticatedAt?: string | null;
} | null> {
	const res = await fetch(`${getApiBase()}/payments/billing/key`, {
		headers: getAuthHeaders(),
	});
	if (res.status === 404) return null;
	if (!res.ok) throw new Error("빌링키 조회 실패");
	return res.json();
}

export async function chargeBilling(amount: number): Promise<{ success?: true; error?: string }> {
	const res = await fetch(`${getApiBase()}/payments/billing/charge`, {
		method: "POST",
		headers: getAuthHeaders(),
		body: JSON.stringify({ amount }),
	});
	const data = await res.json();
	if (!res.ok) return { error: data.error || "결제에 실패했습니다." };
	return { success: true };
}
